import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { ApprovalFlow, User } from '../../../models/api-response.model';
import { ApprovalFlowService } from '../../../services/approval_flow_service';
import { UserManagementService } from '../../../services/user_management_service';

@Component({
  selector: 'app-approval-flow',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './approval-flow.component.html',
  styleUrls: ['./approval-flow.component.scss']
})
export class ApprovalFlowComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly approvalFlowService = inject(ApprovalFlowService);
  private readonly userService = inject(UserManagementService);

  readonly approvalFlows = signal<ApprovalFlow[]>([]);
  readonly users = signal<User[]>([]);
  readonly levelNumbers = computed(() => Array.from({ length: this.levels.length }, (_, index) => index + 1));
  message = '';

  readonly form = this.fb.nonNullable.group({
    module_name: ['', [Validators.required, Validators.minLength(2)]],
    flow_name: ['', [Validators.required, Validators.minLength(2)]],
    description: [''],
    approval_levels: [1, [Validators.required, Validators.min(1), Validators.max(20)]],
    final_approval_authority_user_id: [0, [Validators.required, Validators.min(1)]],
    levels: this.fb.array([this.createLevelGroup(1)])
  });

  get levels(): FormArray {
    return this.form.controls.levels;
  }

  ngOnInit(): void {
    this.load();
  }

  syncLevels(): void {
    const approvalLevels = Number(this.form.controls.approval_levels.value) || 1;
    while (this.levels.length < approvalLevels) {
      this.levels.push(this.createLevelGroup(this.levels.length + 1));
    }
    while (this.levels.length > approvalLevels) {
      this.levels.removeAt(this.levels.length - 1);
    }
    this.levels.controls.forEach((control, index) => {
      control.patchValue({ level_no: index + 1 });
    });
  }

  create(): void {
    this.syncLevels();
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const rawValue = this.form.getRawValue();
    this.approvalFlowService.create({
      module_name: rawValue.module_name,
      flow_name: rawValue.flow_name,
      description: rawValue.description || null,
      approval_levels: rawValue.approval_levels,
      final_approval_authority_user_id: rawValue.final_approval_authority_user_id,
      levels: rawValue.levels
    }).subscribe({
      next: () => {
        this.message = 'Approval flow created successfully.';
        this.form.reset({
          module_name: '',
          flow_name: '',
          description: '',
          approval_levels: 1,
          final_approval_authority_user_id: 0
        });
        this.levels.clear();
        this.levels.push(this.createLevelGroup(1));
        this.loadApprovalFlows();
      },
      error: () => {
        this.message = 'Unable to create approval flow. Please check the selected users and flow name.';
      }
    });
  }

  private createLevelGroup(levelNo: number) {
    return this.fb.nonNullable.group({
      level_no: [levelNo, [Validators.required, Validators.min(1)]],
      approver_user_id: [0, [Validators.required, Validators.min(1)]]
    });
  }

  private load(): void {
    this.loadApprovalFlows();
    this.userService.list().subscribe((response) => {
      this.users.set(response.value === '' ? [] : response.value.filter((user) => user.is_active));
    });
  }

  private loadApprovalFlows(): void {
    this.approvalFlowService.list().subscribe((response) => {
      this.approvalFlows.set(response.value === '' ? [] : response.value);
    });
  }
}
